/*
    Tabs is a decorator function to add the Vue Tabs component.
        * Arguments:
            - {Object} options
        * Usage:
           The 'options' argument is the same configuration for a Vue Component. in addition, the next options:

           - name [string]: name used for Tabs component in Vue. (by default is 'tabs'. Remember considering the prefix option.)

        * Props:

            - current-id-tab: sets the first the currently active tab (by default is '1').
            - active-class: is a CSS class, uses when the tab is active. this uses for passing the same value to the tab components.

        # The Sub-components:

            * tab: the tab component is the tab button, this has the next props:
                - id-tab: is the tab id that links with its content.
                - current-tab: identifies the currently active tab (is required for the tabs functionality).
                - change-tab: is the function that triggers the tab changing (is required for the tabs functionality).
                - active-class: is a CSS class, uses when the tab is active. (by default 'active')

            * tab-content: the tab-content component is the tab content, this has the next props:
                - id-tab: is the tab id that links with its tab button.
                - current-tab: identifies the currently active tab (is required for the tabs functionality).
                - change-tab: is the function that triggers the tab changing (is required for the tabs functionality in cascade mode).
                - tab-class: is a CSS class, uses when the tab button styles. (by default 'tab', only in cascade mode)
                - active-class: is a CSS class, uses when the tab is active. (by default is active)
*/
import {
    content as contentTemplate,
    tab as tabTemplate,
} from './template-strings/tabs-component';

const tabs = ({ name = 'tabs', props, data: dataProp, mounted, methods, components, tabProps, contentProps, tabData, tabContentData, tabOptions, tabContentOptions, ...options} = {}) => {
    return {
        name,
        props: {
            currentIdTab: [String, Number],
            activeClass: String,
            ...props
        },
        data: function() {
            const _dataProp = dataProp && dataProp();
            const data = {
                currentTab: this.currentIdTab || 1,
                ..._dataProp
            };

            return data;
        },
        mounted: function() {
            mounted && mounted.call(this);

            const tabChildren = {};
            this.$children.forEach(c => {
                const tag = c.$vnode.componentOptions.tag;
                if(!['tab', 'tab-content'].includes(tag)){
                    return;
                }

                const id = c.idTab;
                const e = tabChildren[id];

                if(!e){
                    tabChildren[id] = c;
                    return;
                }

                c.setTabInstance(e);
            });
        },
        methods: {
            changeTab: function(id){
                this.currentTab = id;
            },
            ...methods
        },
        components: {
            'tab' : {
                name: 'tab',
                template: tabTemplate,
                props: {
                    idTab: [String, Number],
                    activeClass: {type: String, default: 'active'},
                    currentTab: [String, Number],
                    changeTab: Function,
                    ...tabProps
                },
                data: function() {
                    return {
                        ...tabData,
                    }
                },
                ...tabOptions,
                methods: {
                    onClick: function(){
                        this.changeTab(this.idTab);
                    },
                    setTabInstance: function(e){
                        return e.setTabDefaultSlot(this.getDefaultSlot())
                    },
                    getDefaultSlot: function(){
                        return this.$slots.default;
                    }
                },
            },
            'tab-content' : {
                name: 'tab-content',
                template: contentTemplate,
                props: {
                    idTab: [String, Number],
                    currentTab: [String, Number],
                    tabClass: { type: String, default: 'tab'},
                    activeClass: {type: String, default: 'active'},
                    changeTab: Function,
                    ...contentProps
                },
                data: function() {
                    let data = {
                        tabSlot: undefined,
                        ...tabContentData
                    };

                    return data;
                },
                ...tabContentOptions,
                beforeUpdate: function() {
                    this.$slots.tab = this.tabSlot;
                },
                methods: {
                    onClick: function(){
                        this.changeTab(this.idTab);
                    },
                    setTabInstance: function(e){
                        this.setTabDefaultSlot(e.getDefaultSlot())
                    },
                    setTabDefaultSlot: function(slot){
                        this.tabSlot = slot;
                    }
                },
            },
            ...components
        },
        ...options
    }
};

export default tabs;
